import { useState, useCallback, useRef, useEffect } from 'react'
import { apiClient } from '@/services/api'
import { useErrorHandler } from '@/hooks/useAuth'

import { QRCodeRegenerationStatus } from '@/utils/qr-code/qr-code-regeneration.constant'

interface CheckStatusResponse {
  status: string
  message?: string
}

interface UseBulkImportTrolleysParams {
  onInProgress?: () => void
  onDone?: () => Promise<void>
}

interface UseBulkImportTrolleysReturn {
  startPollingByProcessId: (processId: string) => void
  isLoading: boolean
  error: Nullable<string>
}

const POLLING_INTERVAL = 5000

export function useBulkImportTrolleys(
  params?: UseBulkImportTrolleysParams
): UseBulkImportTrolleysReturn {
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError] = useState<Nullable<string>>(null)
  const { handleError } = useErrorHandler()
  const handleErrorRef = useRef(handleError)
  const shouldContinuePollingRef = useRef<boolean>(false)

  useEffect(() => {
    handleErrorRef.current = handleError
  }, [handleError])

  const checkStatus = useCallback(
    async (processId: string): Promise<string> => {
      try {
        const response = await apiClient.get<CheckStatusResponse>(
          '/trolleys/check-status-bulk-import',
          {
            params: {
              process_id: processId,
              wait_seconds: POLLING_INTERVAL / 1000,
            },
          }
        )
        return response.data.status
      } catch (err) {
        setError(handleErrorRef.current(err))
        return ''
      }
    },
    []
  )

  const pollStatus = useCallback(
    async (processId: string): Promise<void> => {
      if (!shouldContinuePollingRef.current) {
        return
      }

      const status = await checkStatus(processId)
      const normalizedStatus = status.toLowerCase()

      if (normalizedStatus === QRCodeRegenerationStatus.DONE) {
        shouldContinuePollingRef.current = false
        setIsLoading(false)
        if (params?.onDone) {
          await params.onDone()
        }
      } else if (normalizedStatus === QRCodeRegenerationStatus.IN_PROGRESS) {
        if (shouldContinuePollingRef.current) {
          await pollStatus(processId)
        }
      } else {
        shouldContinuePollingRef.current = false
        setIsLoading(false)
      }
    },
    [checkStatus, params]
  )

  const startPolling = useCallback(
    (processId: string) => {
      shouldContinuePollingRef.current = true
      setIsLoading(true)
      pollStatus(processId)
    },
    [pollStatus]
  )

  useEffect(() => {
    return () => {
      shouldContinuePollingRef.current = false
    }
  }, [])

  return {
    startPollingByProcessId: startPolling,
    isLoading,
    error,
  }
}
